import { addDays, addMinutes, addYears, differenceInBusinessDays, differenceInCalendarDays, eachDayOfInterval, endOfMonth, format, formatDuration, formatISO, getYear, isBefore, isValid, isWeekend, minutesToHours, minutesToSeconds, parseISO, previousFriday, set, startOfDay, startOfMonth, subBusinessDays, subWeeks, toDate, } from 'date-fns';
/** Extract the calendar year without shifting a date-only value across time zones. */
export const getHolidayYear = (date) => {
    if (!date)
        return null;
    const value = parseISO(date.slice(0, 10));
    return isValid(value) ? getYear(value) : null;
};
/** Default calendar window: two weeks before today through one calendar year after it. */
export const getDefaultCalendarRange = (today = new Date()) => {
    const date = startOfDay(today);
    return { from: subWeeks(date, 2), to: addYears(date, 1) };
};
/**
 * Format a date as YYYY-MM-dd
 */
export const toISO8601 = (date) => {
    return formatISO(toDate(date), { representation: 'date' });
};
/**
 * Format a date as Monday January 1st 2025
 */
export const toLongDateString = (date) => {
    return format(toDate(date), 'EEEE do LLLL yyyy');
};
/**
 * Format a date as YYYY_MM_dd HH:mm:ss
 */
export const toDateTimeString = (date) => {
    return format(toDate(date), 'yyyy-MM-dd HH:mm:ss');
};
/**
 * Format a date as `Monday January 1st 2025 12:00:00`
 */
export const toLongDateTimeString = (date) => {
    return format(toDate(date), 'EEEE do LLLL yyyy HH:mm:ss');
};
/**
 * Get the last active day of trading (including today).
 */
export const getT = () => {
    const today = new Date();
    const lastWeekday = isWeekend(today) ? previousFriday(today) : today;
    return lastWeekday;
};
/**
 * Get the previous active day of trading (T - 1).
 */
export const getTMinus1 = () => {
    const T = getT();
    return subBusinessDays(T, 1);
};
/**
 * Get the second-last active day of trading (T - 2).
 */
export const getTMinus2 = () => {
    const TMinus1 = getTMinus1();
    return subBusinessDays(TMinus1, 1);
};
/**
 * Get the business days before today with an arbitrary number
 */
export const getTMinus = (days) => {
    return subBusinessDays(getT(), days);
};
export const getFirstDateOfMonth = () => {
    return set(new Date(), { date: 1 });
};
export const getFirstBusinessDateOfMonth = () => {
    let date = set(new Date(), { date: 1 });
    while (isWeekend(date)) {
        date = addDays(date, 1);
    }
    return date;
};
/**
 * Get a shorthand duration from a number of minutes.
 *
 * @example
 * const duration = formatShortDuration(100)
 * // duration = '1 hr 40 mins'
 */
export const formatShortDuration = (minutes) => {
    const number = Math.abs(minutes);
    return formatDuration({
        hours: minutesToHours(number),
        minutes: Math.floor(number % 60),
        seconds: number >= 0 && number < 1 ? minutesToSeconds(number) % 3600 : undefined,
    })
        .replace('minutes', 'mins')
        .replace('minute', 'min')
        .replace('hours', 'hrs')
        .replace('hour', 'hr');
};
/**
 * Get a list of dates, spread out by a time interval
 */
export const getDatesInMinuteRange = (start, end, interval = 30) => {
    let currentDate = start;
    const intervals = [];
    while (currentDate < end) {
        const nextDate = addMinutes(currentDate, interval);
        intervals.push({ start: currentDate, end: nextDate });
        currentDate = nextDate;
    }
    return intervals;
};
/**
 * Get a list of dates, spread out by a day interval, inclusive of end date
 */
export const getDatesInDayRange = (start, end, businessDaysOnly = false) => {
    if (!end)
        return [new Date(start)];
    return eachDayOfInterval({ start: new Date(start), end: new Date(end) }).filter((date) => businessDaysOnly ? !isWeekend(date) : date);
};
/**
 * Get all the dates in a given month as an array
 */
export const getDates = () => {
    const { start, end } = {
        start: startOfMonth(new Date()),
        end: endOfMonth(new Date()),
    };
    const dates = [];
    let currentDate = new Date(start);
    while (isBefore(currentDate, end)) {
        dates.push(currentDate);
        currentDate = addDays(new Date(currentDate), 1);
    }
    return dates;
};
/**
 * Check if Date object is valid date
 */
export const isValidDate = (date) => {
    return isValid(date);
};
/**
 * Get the difference in days between two dates
 */
export const getDifferenceInDays = ({ start, end }, useBusinessDays = false) => {
    const diff = useBusinessDays ? differenceInBusinessDays(end, start) : differenceInCalendarDays(end, start);
    return diff;
};
